import { useEffect, useState } from 'react';
import {
  Download, MonitorDown, Apple, Terminal, ShieldAlert, Camera, Clock,
  AppWindow, Eye, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Loader from '@/components/Loader';

// The installers are uploaded to the web server by hand (they are ~80 MB and are
// not built by CI), so the page discovers them at runtime from a manifest that
// lives beside them rather than being baked into this bundle. Publishing a new
// version is then: copy the installer up, edit one JSON file. No frontend rebuild,
// no redeploy.
//
// Served from the portal's own origin — NOT the API — so this deliberately uses
// plain fetch instead of the axios client, which would prefix VITE_API_URL.
//
//   /var/www/dgs-attendance/downloads/manifest.json
//   {
//     "version": "1.0.0",
//     "releasedAt": "2026-07-13",
//     "notes": "First release",
//     "builds": {
//       "windows": { "file": "Employee-Desk-CRM-Timer-Setup-1.0.0.exe", "sizeMB": 78 },
//       "mac":     null,
//       "linux":   null
//     }
//   }
const MANIFEST_URL = '/downloads/manifest.json';

const PLATFORMS = {
  windows: { label: 'Windows', icon: MonitorDown, hint: 'Windows 10 or 11, 64-bit' },
  mac: { label: 'macOS', icon: Apple, hint: 'macOS 11 or later' },
  linux: { label: 'Ubuntu / Linux', icon: Terminal, hint: 'Ubuntu 20.04+, Xorg session' },
};

function detectPlatform() {
  const ua = `${navigator.userAgent} ${navigator.platform || ''}`.toLowerCase();
  if (ua.includes('mac')) return 'mac';
  if (ua.includes('linux') || ua.includes('x11')) return 'linux';
  return 'windows';
}

export default function DesktopApp() {
  const [manifest, setManifest] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | unavailable
  const mine = detectPlatform();

  useEffect(() => {
    let alive = true;
    fetch(MANIFEST_URL, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error('not published');
        // A missing file behind an SPA fallback comes back as index.html with a
        // 200, so trust the content type rather than the status code.
        if (!res.headers.get('content-type')?.includes('json')) throw new Error('not published');
        return res.json();
      })
      .then((data) => {
        if (!alive) return;
        setManifest(data);
        setState('ready');
      })
      .catch(() => alive && setState('unavailable'));
    return () => {
      alive = false;
    };
  }, []);

  if (state === 'loading') return <Loader />;

  const builds = manifest?.builds || {};
  const primary = builds[mine];
  const others = Object.keys(PLATFORMS).filter((key) => key !== mine && builds[key]);

  return (
    <div>
      <PageHeader
        title="Desktop App"
        subtitle="Employee Desk CRM Timer — records your work time, so you don't have to."
      />

      {state === 'unavailable' ? (
        <Card className="flex items-start gap-4">
          <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={22} />
          <div>
            <div className="font-semibold">The installer has not been published yet</div>
            <p className="text-sm text-slate-500 mt-1">
              Once your administrator uploads it, the download button will appear here. Nothing to do
              in the meantime.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Download */}
          <div className="lg:col-span-2 space-y-5">
            <Card>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <img src="/fevicon.svg" alt="" className="h-14 w-14 object-contain" />
                  <div>
                    <div className="text-lg font-bold">Employee Desk CRM Timer</div>
                    <div className="text-sm text-slate-500">
                      Version {manifest.version}
                      {manifest.releasedAt && ` · released ${manifest.releasedAt}`}
                    </div>
                  </div>
                </div>

                {primary ? (
                  <a
                    href={`/downloads/${primary.file}`}
                    download
                    className="btn-primary text-base px-5 py-2.5"
                  >
                    <Download size={18} />
                    Download for {PLATFORMS[mine].label}
                    {primary.sizeMB ? ` (${primary.sizeMB} MB)` : ''}
                  </a>
                ) : (
                  <div className="text-sm text-slate-500 max-w-xs text-right">
                    No build for {PLATFORMS[mine].label} yet. Ask your administrator.
                  </div>
                )}
              </div>

              {manifest.notes && (
                <p className="text-sm text-slate-500 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                  {manifest.notes}
                </p>
              )}

              {others.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                    Other platforms
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {others.map((key) => {
                      const Icon = PLATFORMS[key].icon;
                      return (
                        <a
                          key={key}
                          href={`/downloads/${builds[key].file}`}
                          download
                          className="btn-secondary"
                        >
                          <Icon size={16} />
                          {PLATFORMS[key].label}
                          {builds[key].sizeMB ? ` · ${builds[key].sizeMB} MB` : ''}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>

            {/* Install steps */}
            <Card>
              <h2 className="font-semibold mb-4">Installing it</h2>
              <ol className="space-y-4">
                <Step n={1} title="Run the installer you just downloaded." />
                <Step
                  n={2}
                  title="Windows will warn you about an “unknown publisher”."
                  icon={ShieldAlert}
                  tone="amber"
                >
                  Click <strong>More info</strong>, then <strong>Run anyway</strong>. This warning appears
                  because the installer is not code-signed yet — not because anything is wrong with it.
                  If you did not download it from this page, do not run it.
                </Step>
                <Step n={3} title="Sign in with your Employee Desk CRM portal account.">
                  The same email and password you used to open this page. There is no separate account.
                </Step>
                <Step n={4} title="Press Start tracking.">
                  This also checks you in for the day. “Stop &amp; check out” at the end ends your day.
                  The app then lives in your system tray and starts with your computer.
                </Step>
              </ol>

              {mine === 'mac' && (
                <PlatformNote tone="sky" title="One extra step on macOS">
                  macOS will ask for <strong>Screen Recording</strong> and <strong>Accessibility</strong>{' '}
                  permission the first time. Both are required — without them the app cannot record your
                  work time correctly. System Settings → Privacy &amp; Security.
                </PlatformNote>
              )}

              {mine === 'linux' && (
                <PlatformNote tone="amber" title="One extra step on Ubuntu">
                  You must be on an <strong>Xorg</strong> session, not Wayland (Ubuntu's default). On the
                  login screen, click the gear icon and choose <strong>“Ubuntu on Xorg”</strong>. On
                  Wayland the app cannot read the system idle timer and your active time would be recorded
                  incorrectly. Also run <code className="text-xs">sudo apt install xdotool</code>.
                </PlatformNote>
              )}
            </Card>
          </div>

          {/* Transparency — the point of this column is that nobody has to guess. */}
          <div className="space-y-5">
            <Card>
              <h2 className="font-semibold mb-1">What it records</h2>
              <p className="text-xs text-slate-500 mb-4">Only while tracking is started by you.</p>
              <ul className="space-y-3">
                <Records icon={Clock} title="Active and idle time">
                  Measured from your keyboard and mouse activity.
                </Records>
                <Records icon={AppWindow} title="The app you are working in">
                  Its name and window title — so your day can be attributed to real work.
                </Records>
                <Records icon={Camera} title="A screenshot every ~5 minutes">
                  You get a notification every single time, and you can see every image in the app.
                </Records>
              </ul>
            </Card>

            <Card>
              <h2 className="font-semibold mb-4">What it never does</h2>
              <ul className="space-y-3">
                <Never>
                  Record what you type. Keystrokes are <strong>counted</strong>, never captured — no
                  password or message ever leaves your machine.
                </Never>
                <Never>Touch your camera or microphone.</Never>
                <Never>Run when you have stopped it, or while your machine is asleep.</Never>
                <Never>Hide. The tray icon is always visible while it is recording.</Never>
              </ul>
              <p className="text-xs text-slate-500 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex gap-2">
                <Eye size={14} className="shrink-0 mt-0.5" />
                <span>
                  You can see every screenshot taken of your screen in the app's Screenshots tab — the
                  same images your manager sees.
                </span>
              </p>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function Step({ n, title, children, icon: Icon, tone }) {
  const tones = {
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
  };
  return (
    <li className="flex gap-3">
      <div
        className={
          tone
            ? `w-7 h-7 rounded-full grid place-items-center shrink-0 ${tones[tone]}`
            : 'w-7 h-7 rounded-full grid place-items-center shrink-0 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 text-sm font-semibold'
        }
      >
        {Icon ? <Icon size={15} /> : n}
      </div>
      <div className="pt-0.5">
        <div className="text-sm font-medium">{title}</div>
        {children && <p className="text-sm text-slate-500 mt-1">{children}</p>}
      </div>
    </li>
  );
}

function PlatformNote({ tone, title, children }) {
  const tones = {
    sky: 'bg-sky-50 border-sky-200 dark:bg-sky-500/10 dark:border-sky-500/20',
    amber: 'bg-amber-50 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/20',
  };
  return (
    <div className={`mt-5 rounded-xl border p-4 ${tones[tone]}`}>
      <div className="text-sm font-semibold mb-1">{title}</div>
      <p className="text-sm text-slate-600 dark:text-slate-300">{children}</p>
    </div>
  );
}

function Records({ icon: Icon, title, children }) {
  return (
    <li className="flex gap-3">
      <Icon size={16} className="text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" />
      <div>
        <div className="text-sm font-medium">{title}</div>
        <p className="text-xs text-slate-500 mt-0.5">{children}</p>
      </div>
    </li>
  );
}

function Never({ children }) {
  return (
    <li className="flex gap-3">
      <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
      <p className="text-sm text-slate-600 dark:text-slate-300">{children}</p>
    </li>
  );
}
